import { globalSpinnerController } from '@/components/Spinner';
import { sharedData, socialSignInError } from '@/shares/dataManager';
import {
  linkWithCredential,
  type UserCredential,
  OAuthProvider,
} from 'firebase/auth';
import { idTokenHandler } from './authService';
import { ACTION_TYPES } from '@/constants/actions';
import { isAppleDeviceWebApp } from '@/utils/uaParser';
import { postTokensToIOSApp } from '@/utils/IOSwebkitMessageHandler';
import getIsNeedOnboarding from '@/utils/getIsNeedOnboarding';
import { PAGE_PATHS } from '@/constants/url';
import { moveToAndroidLoginSuccessPage } from './androidAppService';

const moveToEmailLinkAccountPage = (from: 'androidApp' | 'web') => {
  const emailLinkAccountPageUrl = new URL(window.location.href);
  emailLinkAccountPageUrl.pathname = '/login';
  emailLinkAccountPageUrl.searchParams.set('mode', 'link');
  emailLinkAccountPageUrl.searchParams.set('from', from);

  globalSpinnerController.hideSpinner();
  window.location.href = emailLinkAccountPageUrl.href;
};

const linkEmailAccount = async ({
  emailLoginUserCredential,
  from,
}: {
  emailLoginUserCredential: UserCredential;
  from: string | null;
}) => {
  try {
    if (socialSignInError === null) {
      throw new Error(
        'No social sign-in error found for linking email account.',
      );
    }

    const credentialForLinkAccount =
      OAuthProvider.credentialFromError(socialSignInError);

    if (credentialForLinkAccount === null) {
      throw new Error('No credential available for linking the account.');
    }

    const linkCredential = await linkWithCredential(
      emailLoginUserCredential.user,
      credentialForLinkAccount,
    );

    const idToken = await linkCredential.user.getIdToken(true);

    const { customToken } = await idTokenHandler({
      idToken,
      actionType: ACTION_TYPES.SOCIAL_SIGNIN,
    });

    if (isAppleDeviceWebApp) {
      await postTokensToIOSApp({
        idToken,
        customToken,
      });
    }

    const isFromAndroidApp = from === 'androidApp';

    if (isFromAndroidApp) {
      moveToAndroidLoginSuccessPage({
        customToken,
        providerType: credentialForLinkAccount.providerId,
      });
      return;
    }

    moveToLinkSuccessPage({
      idToken,
      from,
      uid: linkCredential.user.uid,
      linkedMethod: credentialForLinkAccount.providerId,
    });
  } catch (error) {
    globalSpinnerController.hideSpinner();
    throw error;
  }
};

const moveToLinkSuccessPage = async ({
  idToken,
  from,
  uid,
  linkedMethod,
}: {
  idToken: string;
  from: string | null;
  uid: string;
  linkedMethod: string;
}) => {
  const { callbackUrl } = sharedData.queries;
  const isFromWeb = from === 'web';

  //* 안드로이드 로그인이 아닌 경우엔 항상 callbackUrl이 존재해야 함
  if (callbackUrl === null) {
    throw new Error(
      'No callback URL found for redirection after linking account.',
    );
  }

  const updatedCallbackUrl = new URL(callbackUrl);
  updatedCallbackUrl.searchParams.set('idToken', idToken);

  const isNeedOnboarding = await getIsNeedOnboarding({
    uid,
    token: idToken,
  });

  if (isNeedOnboarding) {
    updatedCallbackUrl.pathname = '/onboarding';
  }

  const redirectUrl = isFromWeb
    ? `${PAGE_PATHS.CONNECT_DONE}?callbackUrl=${encodeURIComponent(
        updatedCallbackUrl.toString(),
      )}&linkedMethod=${linkedMethod}`
    : updatedCallbackUrl.toString();

  window.location.href = redirectUrl;
};

export { moveToEmailLinkAccountPage, moveToLinkSuccessPage, linkEmailAccount };
